import { useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { customInstance } from '@edison/api-client-react';
import { Button } from '@/components/ui/button.js';
import { formatFy } from '@/lib/utils.js';
import { CLAIM_MODE_META, type ClaimMode } from '@/store/claim-mode-store.js';
import { Printer, AlertCircle, TriangleAlert } from 'lucide-react';

const modeLabel = (m: string) => CLAIM_MODE_META[m as ClaimMode]?.label ?? m;
// Humanise raw snake_case enum keys (stage, ipOwnership) for display, e.g. "problem_definition" → "Problem definition".
const humanize = (s: string) => s ? s.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase()) : s;

interface Competence { assessorName: string | null; roleOnActivity: string; uncertaintyBasis: string }
interface Activity { name: string; description: string | null; ipOwnership: string; competence: Competence[] }
interface Deliberation { panelistName: string | null; responses: unknown; overallComment: string | null; submittedAt: string | null }
interface Tfa {
  stage: string; locked: boolean; retrospective: boolean;
  evidencePeriodStart: string | null; evidencePeriodEnd: string | null; assessmentDate: string | null;
  problemStatement: string | null; cotsSweepNotes: string | null;
  panelists: Array<{ name: string | null; confirmedAt: string | null; isFinalApprover: boolean }>;
  deliberations: Deliberation[];
  determination: { determination: string; notes: string | null; determinedByName: string | null; determinedAt: string | null } | null;
  dissents: Array<{ panelistName: string | null; statement: string }>;
}
interface ExportProject {
  id: string; name: string; incomeYear: string; description: string | null;
  tfa: Tfa | null; activities: Activity[]; contributors: Array<{ name: string | null; email: string | null }>;
  totalHours: number; rdHours: number;
}
interface ExportData {
  generatedAt: string;
  mode: string;
  incomeYear: string | null;
  projects: ExportProject[];
  // apportionment mode only: projectIds with captured effort whose R&D share isn't adopted yet —
  // when present, the labour/R&D figures are zero pending adoption and the header warning renders.
  apportionmentAwaitingProjects?: string[];
}

const DET_LABEL: Record<string, string> = {
  uncertain: 'Uncertain — R&D eligible',
  partially_uncertain: 'Partially uncertain',
  certain: 'Certain — ineligible',
};
// Print-safe badge colours (inline so print-color-adjust keeps them). Green = eligible, amber =
// partial, red = ineligible, slate = no determination.
const DET_STYLE: Record<string, { bg: string; fg: string; bd: string }> = {
  uncertain: { bg: '#ecfdf5', fg: '#047857', bd: '#a7f3d0' },
  partially_uncertain: { bg: '#fffbeb', fg: '#b45309', bd: '#fde68a' },
  certain: { bg: '#fef2f2', fg: '#b91c1c', bd: '#fecaca' },
};
function DetBadge({ determination }: { determination: string | null }) {
  const s = (determination && DET_STYLE[determination]) || { bg: '#f1f5f9', fg: '#475569', bd: '#e2e8f0' };
  return (
    <span
      className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold whitespace-nowrap"
      style={{ background: s.bg, color: s.fg, borderColor: s.bd, printColorAdjust: 'exact', WebkitPrintColorAdjust: 'exact' }}
    >
      {determination ? (DET_LABEL[determination] ?? determination) : 'No determination'}
    </span>
  );
}
// Small uppercase section label with an accent tick.
function Label({ children }: { children: ReactNode }) {
  return (
    <p className="export-caption flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
      <span className="inline-block h-3 w-0.5 rounded-full bg-primary/70" aria-hidden="true" />
      {children}
    </p>
  );
}

// A single labelled TFA field. The caption stays with the start of its prose (print rules), while the
// prose itself is free to flow across a page break — so a long problem statement no longer forces the
// whole project onto the next page. `byline` renders a subtle attribution under the prose.
function Field({ label, children, byline }: { label: string; children: ReactNode; byline?: string }) {
  return (
    <div className="export-field">
      <p className="export-caption text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">{label}</p>
      <p className="text-sm leading-relaxed whitespace-pre-wrap text-foreground/90">{children}</p>
      {byline && <p className="text-xs text-muted-foreground mt-1">{byline}</p>}
    </div>
  );
}

function fyOptions(): string[] {
  const now = new Date();
  const endYear = now.getUTCMonth() >= 6 ? now.getUTCFullYear() + 1 : now.getUTCFullYear();
  return [endYear, endYear - 1, endYear - 2].map((y) => `FY${y}`);
}

export function ProjectExportPage() {
  const [incomeYear, setIncomeYear] = useState(fyOptions()[0]!);
  const [showContributors, setShowContributors] = useState(true);
  const [showActivities, setShowActivities] = useState(true);
  const [showTime, setShowTime] = useState(true);

  const { data, isLoading, error } = useQuery({
    queryKey: ['project-export', incomeYear],
    queryFn: () => customInstance<{ data: ExportData }>({ url: `/api/project-export?incomeYear=${incomeYear}`, method: 'GET' }),
  });

  const doc = data?.data;

  return (
    <div className="space-y-6">
      {/* Print stylesheet: when printing, hide everything except #export-doc (robust vs the app shell). */}
      <style>{`@media print {
        body * { visibility: hidden !important; }
        #export-doc, #export-doc * { visibility: visible !important; }
        #export-doc { position: absolute; left: 0; top: 0; width: 100%; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        /* Let a project FLOW across pages instead of jumping whole (which left blank half-pages). */
        .export-project { break-inside: auto; overflow: visible !important; }
        /* Keep small, atomic units together … */
        .export-block { break-inside: avoid; }
        /* … keep the project header with the content that follows it … */
        .export-project-header { break-inside: avoid; break-after: avoid; }
        /* … never orphan a section caption at the foot of a page … */
        .export-caption { break-after: avoid; }
        /* … and let long prose split cleanly with at least 3 lines either side of a break. */
        #export-doc p, #export-doc li { orphans: 3; widows: 3; }
        #export-doc h3 { break-after: avoid; }
      }`}</style>

      {/* Controls (not printed) */}
      <div className="no-print flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Project export</h1>
          <p className="text-sm text-muted-foreground mt-1">Active R&D projects — name, full TFA, and optional sections. Print or save as PDF.</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <select value={incomeYear} onChange={(e) => setIncomeYear(e.target.value)} className="h-8 rounded-md border border-border bg-background px-3 text-sm" aria-label="Income year">
            {fyOptions().map((fy) => <option key={fy} value={fy}>{formatFy(fy)}</option>)}
          </select>
          <label className="text-sm flex items-center gap-1.5"><input type="checkbox" checked={showContributors} onChange={(e) => setShowContributors(e.target.checked)} /> Who worked on this</label>
          <label className="text-sm flex items-center gap-1.5"><input type="checkbox" checked={showActivities} onChange={(e) => setShowActivities(e.target.checked)} /> Activities</label>
          <label className="text-sm flex items-center gap-1.5"><input type="checkbox" checked={showTime} onChange={(e) => setShowTime(e.target.checked)} /> Total time</label>
          <Button variant="primary" size="sm" onClick={() => window.print()} disabled={!doc?.projects.length}>
            <Printer className="h-4 w-4" /> Print / Save as PDF
          </Button>
        </div>
      </div>

      {isLoading && <div className="h-40 rounded-lg skeleton" />}
      {error && (
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 flex items-center gap-2 text-destructive text-sm">
          <AlertCircle className="h-4 w-4" /> Failed to load export.
        </div>
      )}

      {doc && (
        <div id="export-doc" className="bg-background text-foreground">
          {/* Cover header */}
          <div className="mb-8 print:mb-6 rounded-2xl border border-border overflow-hidden shadow-sm">
            <div
              className="px-7 py-7 text-white"
              style={{ background: 'linear-gradient(120deg, #4f46e5 0%, #7c3aed 55%, #9333ea 100%)', printColorAdjust: 'exact', WebkitPrintColorAdjust: 'exact' }}
            >
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-white/75">Edison · R&amp;D Tax Incentive</p>
              <h2 className="text-3xl font-bold tracking-tight mt-1.5">R&amp;D Project Report</h2>
              <p className="text-base text-white/85 mt-1">{doc.incomeYear ? formatFy(doc.incomeYear) : 'All income years'}</p>
            </div>
            <div className="flex flex-wrap items-center gap-x-8 gap-y-1.5 text-xs text-muted-foreground px-7 py-3.5 bg-muted/30" style={{ printColorAdjust: 'exact', WebkitPrintColorAdjust: 'exact' }}>
              <span><strong className="text-foreground text-sm">{doc.projects.length}</strong> active project{doc.projects.length === 1 ? '' : 's'}</span>
              {showTime && <span>Time basis: <strong className="text-foreground">{modeLabel(doc.mode)}</strong></span>}
              <span>Generated {new Date(doc.generatedAt).toLocaleString('en-AU')}</span>
            </div>
          </div>

          {/* Apportionment awaiting-adoption warning — inside #export-doc so it PRINTS with the
              document (Rule Zero: a printable evidence document must never show unexplained zero
              R&D figures). Icon + text, never colour-only. */}
          {(doc.apportionmentAwaitingProjects?.length ?? 0) > 0 && (
            <div
              className="export-block mb-6 rounded-md border border-amber-200 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-900/40 p-3 flex items-start gap-2"
              style={{ printColorAdjust: 'exact', WebkitPrintColorAdjust: 'exact' }}
            >
              <TriangleAlert className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" aria-hidden="true" />
              <div className="text-sm text-amber-800 dark:text-amber-300">
                <p className="font-semibold">R&amp;D shares not yet adopted — labour/R&amp;D figures show zero pending adoption</p>
                <p className="text-xs mt-0.5">
                  Projects awaiting adoption:{' '}
                  {doc.apportionmentAwaitingProjects!
                    .map((pid) => doc.projects.find((pr) => pr.id === pid)?.name ?? pid.slice(0, 8))
                    .join(', ')}
                </p>
              </div>
            </div>
          )}

          {doc.projects.length === 0 && <p className="text-sm text-muted-foreground">No active R&D projects for this year.</p>}

          <div className="space-y-6">
            {doc.projects.map((p, idx) => (
              <section key={p.id} className="export-project rounded-xl border border-border overflow-hidden">
                {/* Project header bar */}
                <div className="export-project-header flex items-start justify-between gap-4 border-b border-border bg-muted/40 px-5 py-3.5" style={{ printColorAdjust: 'exact', WebkitPrintColorAdjust: 'exact' }}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-mono text-muted-foreground">{String(idx + 1).padStart(2, '0')}</span>
                      <h3 className="text-base font-semibold truncate">{p.name}</h3>
                    </div>
                    {p.description && <p className="text-sm text-muted-foreground mt-0.5 line-clamp-2">{p.description}</p>}
                  </div>
                  {p.tfa && <DetBadge determination={p.tfa.determination?.determination ?? null} />}
                </div>

                <div className="px-5 py-4 space-y-4">
                  {/* Stat strip */}
                  {showTime && (
                    <div className="export-block flex flex-wrap gap-6 rounded-lg bg-muted/30 px-4 py-2.5" style={{ printColorAdjust: 'exact', WebkitPrintColorAdjust: 'exact' }}>
                      <div><div className="text-lg font-semibold tabular-nums leading-none">{p.totalHours.toFixed(1)}<span className="text-xs font-normal text-muted-foreground">h</span></div><div className="text-[11px] text-muted-foreground mt-0.5">Total worked</div></div>
                      <div><div className="text-lg font-semibold tabular-nums leading-none">{p.rdHours.toFixed(1)}<span className="text-xs font-normal text-muted-foreground">h</span></div><div className="text-[11px] text-muted-foreground mt-0.5">R&amp;D ({modeLabel(doc.mode)})</div></div>
                      {showContributors && <div><div className="text-lg font-semibold tabular-nums leading-none">{p.contributors.length}</div><div className="text-[11px] text-muted-foreground mt-0.5">Contributors</div></div>}
                    </div>
                  )}

                  {/* TFA */}
                  <div className="space-y-3">
                    <Label>Technical Feasibility Assessment</Label>
                    {!p.tfa ? (
                      <p className="text-sm text-muted-foreground italic">No TFA panel created for this project.</p>
                    ) : (
                      <div className="space-y-4 pl-3 border-l-2 border-primary/20">
                        {/* Meta row — small, kept together */}
                        <div className="export-block flex flex-wrap gap-x-5 gap-y-1 text-xs text-muted-foreground">
                          <span>Stage: <strong className="text-foreground">{humanize(p.tfa.stage)}{p.tfa.locked ? ' · locked' : ''}</strong></span>
                          {p.tfa.retrospective && <span className="text-amber-600 font-medium">Retrospective</span>}
                          {(p.tfa.evidencePeriodStart || p.tfa.evidencePeriodEnd) && <span>Evidence: <strong className="text-foreground">{p.tfa.evidencePeriodStart ?? '?'} → {p.tfa.evidencePeriodEnd ?? '?'}</strong></span>}
                        </div>
                        {/* Long-prose fields — caption stays with prose, prose flows across pages */}
                        {p.tfa.problemStatement && <Field label="Problem statement / technical uncertainty">{p.tfa.problemStatement}</Field>}
                        {p.tfa.cotsSweepNotes && <Field label="COTS sweep">{p.tfa.cotsSweepNotes}</Field>}
                        {p.tfa.panelists.length > 0 && (
                          <div className="export-field">
                            <p className="export-caption text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">Panel</p>
                            <ul className="text-sm space-y-0.5">
                              {p.tfa.panelists.map((pl, i) => <li key={i} className="export-block">{pl.name ?? '—'}{pl.isFinalApprover ? ' · final approver' : ''} <span className={pl.confirmedAt ? 'text-emerald-600' : 'text-muted-foreground'}>{pl.confirmedAt ? '· confirmed' : '· unconfirmed'}</span></li>)}
                            </ul>
                          </div>
                        )}
                        {p.tfa.deliberations.length > 0 && (
                          <div className="export-field">
                            <p className="export-caption text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">Deliberation</p>
                            <ul className="text-sm space-y-1.5">
                              {p.tfa.deliberations.map((d, i) => <li key={i} className="leading-relaxed"><strong className="text-foreground">{d.panelistName ?? '—'}:</strong> {d.overallComment ?? '(responses recorded)'}{d.submittedAt ? '' : ' — not yet submitted'}</li>)}
                            </ul>
                          </div>
                        )}
                        {p.tfa.determination?.notes && (
                          <Field
                            label="Determination rationale"
                            byline={`— ${p.tfa.determination.determinedByName ?? '—'}${p.tfa.determination.determinedAt ? `, ${new Date(p.tfa.determination.determinedAt).toLocaleDateString('en-AU')}` : ''}`}
                          >
                            {p.tfa.determination.notes}
                          </Field>
                        )}
                        {p.tfa.dissents.length > 0 && (
                          <div className="export-block rounded-md border border-amber-200 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-900/40 p-2.5" style={{ printColorAdjust: 'exact', WebkitPrintColorAdjust: 'exact' }}><p className="text-[11px] font-semibold text-amber-700 dark:text-amber-400 mb-0.5">Dissent</p>
                            <ul className="text-sm space-y-1">{p.tfa.dissents.map((d, i) => <li key={i}><strong>{d.panelistName ?? '—'}:</strong> {d.statement}</li>)}</ul>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {showActivities && p.activities.length > 0 && (
                    <div className="space-y-2">
                      <Label>Activities</Label>
                      <ul className="space-y-1.5 pl-3 border-l-2 border-border">
                        {p.activities.map((a, i) => (
                          <li key={i} className="export-block text-sm">
                            <span className="font-medium">{a.name}</span> <span className="text-xs text-muted-foreground">· {humanize(a.ipOwnership)} IP</span>
                            {a.competence.map((c, j) => <div key={j} className="text-xs text-muted-foreground pl-3">{c.assessorName ?? '—'} — {c.roleOnActivity}</div>)}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {showContributors && p.contributors.length > 0 && (
                    <div className="export-block space-y-1.5">
                      <Label>Who worked on this</Label>
                      <div className="flex flex-wrap gap-1.5 pl-3">
                        {p.contributors.map((c, i) => <span key={i} className="inline-flex rounded-full bg-muted px-2.5 py-0.5 text-xs" style={{ printColorAdjust: 'exact', WebkitPrintColorAdjust: 'exact' }}>{c.name}</span>)}
                      </div>
                    </div>
                  )}
                </div>
              </section>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
